import type { FailureCard, PipelineStage } from "../../types";
import { uid } from "../storage";

export interface RepairResult {
  failure: FailureCard;
  message: string;
}

export function seedFailureCards(): FailureCard[] {
  const now = new Date().toISOString();
  return [
    {
      id: "failure_demo_1",
      directiveId: "directive_demo_3",
      title: "TypeScript strict mode violations",
      description: "12 unused imports and 3 null-check gaps in merge planner module.",
      severity: "high",
      stage: "testRepair",
      repairActions: [
        {
          id: "repair_lint",
          label: "Run lint autofix",
          description: "Apply ESLint --fix across src/",
        },
        {
          id: "repair_types",
          label: "Patch null guards",
          description: "Add explicit guards on merge inputs",
        },
      ],
      status: "open",
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "failure_demo_2",
      directiveId: "directive_demo_2",
      title: "Wrangler deploy dry-run mismatch",
      description: "Worker binding names differ between wrangler.toml and export manifest.",
      severity: "medium",
      stage: "packaging",
      repairActions: [
        {
          id: "repair_sync_bindings",
          label: "Sync bindings",
          description: "Align KV/D1 binding names in wrangler.toml",
        },
        {
          id: "repair_rebuild",
          label: "Rebuild export bundle",
          description: "Regenerate handoff pack from artifact planner",
        },
      ],
      status: "open",
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "failure_demo_3",
      title: "Stale localStorage schema",
      description: "v1 state missing directives/agents — migration shim required on load.",
      severity: "low",
      stage: "intake",
      repairActions: [
        {
          id: "repair_migrate",
          label: "Run state migration",
          description: "Merge defaults for v2 fields on hydrate",
        },
      ],
      status: "resolved",
      createdAt: now,
      updatedAt: now,
    },
  ];
}

export function createFailureForStage(
  directiveId: string | undefined,
  stage: PipelineStage,
  title: string,
  description: string,
): FailureCard {
  const now = new Date().toISOString();
  return {
    id: uid("failure"),
    directiveId,
    title,
    description,
    severity: "medium",
    stage,
    repairActions: [
      {
        id: uid("action"),
        label: "Auto-repair pass",
        description: "Run deterministic local repair simulation",
      },
      {
        id: uid("action"),
        label: "Rollback stage",
        description: "Move directive back one pipeline stage",
      },
    ],
    status: "open",
    createdAt: now,
    updatedAt: now,
  };
}

export function applyRepairAction(failure: FailureCard, actionId: string): RepairResult {
  const action = failure.repairActions.find((a) => a.id === actionId);
  if (!action) {
    return { failure, message: "Unknown repair action." };
  }

  const now = new Date().toISOString();
  const repaired: FailureCard = {
    ...failure,
    status: "repairing",
    updatedAt: now,
  };

  const resolved: FailureCard = {
    ...repaired,
    status: "resolved",
    updatedAt: new Date().toISOString(),
    description: `${failure.description}\n\n[Repaired via ${action.label}] ${action.description}`,
  };

  return {
    failure: resolved,
    message: `Applied "${action.label}" — failure marked resolved.`,
  };
}

export function escalateFailure(failure: FailureCard): FailureCard {
  return {
    ...failure,
    status: "escalated",
    severity: failure.severity === "critical" ? "critical" : "high",
    updatedAt: new Date().toISOString(),
    description: `${failure.description}\n\n[Escalated] Routed to BOB for manual review.`,
  };
}

export function updateFailure(
  failures: FailureCard[],
  id: string,
  updater: (f: FailureCard) => FailureCard,
): FailureCard[] {
  return failures.map((f) => (f.id === id ? updater(f) : f));
}
